import { useRouter } from 'expo-router'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { ScrollView, View, type LayoutRectangle } from 'react-native'

import { ICON } from '@/src/general/enums/icons'
import { useOnChange } from '@/src/general/hooks/useOnChange'
import { cn } from '@/src/general/utils/cn'

import { Button } from '../button/default'
import { Typography } from '../text/Typography'
import { useDebouncedWidth } from './hooks/useDebouncedWidth'
import type { TSliderProps } from './Slider'
import {
  getAutoScrollTarget,
  getAutoScrollTargetX,
  getNextScrollTarget,
  getPreviousScrollTarget,
  setLayoutAtIndex,
} from './Slider.utils'

export const DesktopSlider = <TItem,>({
  title,
  color = 'default',
  buttonsColor = 'default',
  list,
  renderItem,
  renderPlaceholderItem,
  getItemKey,
  link,
  emptyState,
  testID,
  maxNbItems = 10,
  classes,
  linkButton,
  autoScrollIndex,
  containerProps: { className: containerClassName, onLayout: containerOnLayout, ...containerProps } = {},
}: TSliderProps<TItem>): ReactNode => {
  const [scrollViewWidth, setScrollViewWidth] = useState(0)
  const [selectedScrollIndex, setSelectedScrollIndex] = useState(0)
  const [coordinates, setCoordinates] = useState<LayoutRectangle[]>([])
  const scrollViewRef = useRef<ScrollView>(null)
  const [hasAutoScrolled, setHasAutoScrolled] = useState(false)
  const { navigate } = useRouter()

  // Allow auto-scroll again when the target index changes (e.g. after a new publication is prayed).
  useOnChange(autoScrollIndex, (prevAutoScrollIndex) => {
    if (typeof prevAutoScrollIndex === 'number') {
      setHasAutoScrolled(false)
    }
  })

  const handleScrollPrevious = () => {
    const target = getPreviousScrollTarget({ selectedScrollIndex, coordinates })
    if (!target || !scrollViewRef.current) return

    scrollViewRef.current.scrollTo({ x: target.x, animated: true })
    setSelectedScrollIndex(target.index)
  }

  const handleScrollNext = () => {
    const target = getNextScrollTarget({ selectedScrollIndex, coordinates, innerContainerWidth, scrollViewWidth })
    if (!target || !scrollViewRef.current) return

    scrollViewRef.current.scrollTo({ x: target.x, animated: true })
    setSelectedScrollIndex(target.index)
  }

  const listWithLimitedItems = list?.slice(0, maxNbItems) || []

  const { debouncedSetWidth, innerContainerWidth } = useDebouncedWidth()

  useEffect(() => {
    const autoScrollTarget = getAutoScrollTarget({
      autoScrollIndex,
      coordinates,
      itemCount: listWithLimitedItems.length,
    })

    if (
      hasAutoScrolled ||
      !autoScrollTarget ||
      scrollViewWidth === 0 ||
      innerContainerWidth === 0 ||
      !scrollViewRef.current
    ) {
      return
    }

    // If the target item is near the end, its x position can be greater than the
    // maximum scrollable offset. Clamp it to the last valid scroll position.
    const targetX = getAutoScrollTargetX({
      targetItemX: autoScrollTarget.coordinate.x,
      innerContainerWidth,
      scrollViewWidth,
    })

    scrollViewRef.current.scrollTo({ x: autoScrollIndex === 0 ? 0 : targetX, animated: true })
    // Keep the arrow navigation state aligned with the item selected by the automatic scroll.
    setSelectedScrollIndex(autoScrollTarget.index)
    setHasAutoScrolled(true)
  }, [autoScrollIndex, coordinates, hasAutoScrolled, innerContainerWidth, listWithLimitedItems.length, scrollViewWidth])

  const sliderContent = (
    <View
      className={cn('flex flex-row gap-4 py-4', classes?.container, containerClassName)}
      onLayout={(event) => {
        containerOnLayout?.(event)
        const { layout } = event.nativeEvent
        debouncedSetWidth(layout.width)
      }}
      {...containerProps}
    >
      {!list
        ? Array.from({ length: 8 }, (_, index) => renderPlaceholderItem(index))
        : listWithLimitedItems.map((item, index) => (
            <View
              className={classes?.slot}
              onLayout={(event) => {
                setCoordinates((prev) => setLayoutAtIndex(prev, index, event.nativeEvent.layout))
              }}
              key={getItemKey(item)}
              testID={testID ? `${testID}-item-${index}` : undefined}
            >
              {renderItem(item, index)}
            </View>
          ))}
    </View>
  )

  return (
    <View className={cn('w-full', classes?.base)} testID={testID}>
      <View className={cn('flex w-full flex-row items-end justify-between gap-4 px-6 pt-8', classes?.titleContainer)}>
        <View className="flex flex-row items-baseline gap-4">
          <Typography colors={color} heading="h1" bold contrast="high" size="lg" className={classes?.title}>
            {title}
          </Typography>
          {linkButton}
          {link && !linkButton && (
            <Button colors={color} variant="faded" size="xs" contrast="low" onPress={() => navigate(link)}>
              <FormattedMessage id="words.see-all.sing" />
            </Button>
          )}
        </View>
        {list && list.length > 0 && (
          <View className="flex flex-row items-center justify-center gap-2">
            <Button
              isIconOnly
              testID={testID ? `${testID}-previous-button` : undefined}
              startIcon={ICON.CHEVRON_LEFT}
              colors={buttonsColor}
              variant="faded"
              onPress={handleScrollPrevious}
            />
            <Button
              isIconOnly
              testID={testID ? `${testID}-next-button` : undefined}
              startIcon={ICON.CHEVRON_RIGHT}
              colors={buttonsColor}
              variant="faded"
              onPress={handleScrollNext}
            />
          </View>
        )}
      </View>
      {listWithLimitedItems.length === 0 && <View className="mt-6">{emptyState || null}</View>}
      {listWithLimitedItems.length === 1 && sliderContent}
      {(!list || listWithLimitedItems.length > 1) && (
        <ScrollView
          ref={scrollViewRef}
          horizontal
          decelerationRate="fast"
          showsHorizontalScrollIndicator={false}
          pagingEnabled
          scrollEnabled={false}
          className={classes?.slider}
          onLayout={({ nativeEvent }) => setScrollViewWidth(nativeEvent.layout.width)}
          testID={testID ? `${testID}-scroll-view` : undefined}
        >
          {sliderContent}
        </ScrollView>
      )}
    </View>
  )
}
